"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import type { SwitchProps as AriaSwitchProps } from "react-aria-components";
import { Switch as AriaSwitch } from "react-aria-components";
import { cx } from "@/utils/cx";
interface ToggleBaseProps {
  size?: "sm" | "md";
  slim?: boolean;
  className?: string;
  isHovered?: boolean;
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
}
export const ToggleBase = t0 => {
  const $ = _c(17);
  const {
    className,
    isHovered,
    isDisabled,
    isFocusVisible,
    isSelected,
    slim,
    size: t1
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== className || $[1] !== isDisabled || $[2] !== isFocusVisible || $[3] !== isHovered || $[4] !== isSelected || $[5] !== size || $[6] !== slim) {
    const styles = {
      default: {
        sm: {
          root: "h-5 w-9 p-0.5",
          switch: cx("size-4", isSelected && "translate-x-4")
        },
        md: {
          root: "h-6 w-11 p-0.5",
          switch: cx("size-5", isSelected && "translate-x-5")
        }
      },
      slim: {
        sm: {
          root: "h-4 w-8",
          switch: cx("size-4", isSelected && "translate-x-4")
        },
        md: {
          root: "h-5 w-10",
          switch: cx("size-5", isSelected && "translate-x-5")
        }
      }
    };
    const classes = slim ? styles.slim[size] : styles.default[size];
    t4 = cx("cursor-pointer rounded-full bg-tertiary outline-focus-ring transition duration-150 ease-linear", isSelected && "bg-brand-solid", isSelected && isHovered && "bg-brand-solid_hover", isDisabled && "cursor-not-allowed bg-disabled", isFocusVisible && "outline-2 outline-offset-2", slim && "ring-1 ring-secondary ring-inset", slim && isSelected && "ring-transparent", classes.root, className);
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        transition: "transform 0.15s ease-in-out, translate 0.15s ease-in-out, border-color 0.1s linear, background-color 0.1s linear"
      };
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t3 = cx("rounded-full bg-fg-white shadow-sm", isDisabled && "bg-toggle-button-fg_disabled", slim && "shadow-xs", slim && "border border-toggle-border", slim && isSelected && "border-toggle-slim-border_pressed", slim && isSelected && isHovered && "border-toggle-slim-border_pressed-hover", classes.switch);
    $[0] = className;
    $[1] = isDisabled;
    $[2] = isFocusVisible;
    $[3] = isHovered;
    $[4] = isSelected;
    $[5] = size;
    $[6] = slim;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3) {
    t5 = <div style={t2} className={t3} />;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
interface ToggleProps extends AriaSwitchProps {
  size?: "sm" | "md";
  label?: string;
  hint?: ReactNode;
  slim?: boolean;
}
export const Toggle = t0 => {
  const $ = _c(20);
  let ariaSwitchProps;
  let className;
  let hint;
  let label;
  let slim;
  let t1;
  if ($[0] !== t0) {
    ({
      label,
      hint,
      className,
      size: t1,
      slim,
      ...ariaSwitchProps
    } = t0);
    $[0] = t0;
    $[1] = ariaSwitchProps;
    $[2] = className;
    $[3] = hint;
    $[4] = label;
    $[5] = slim;
    $[6] = t1;
  } else {
    ariaSwitchProps = $[1];
    className = $[2];
    hint = $[3];
    label = $[4];
    slim = $[5];
    t1 = $[6];
  }
  const size = t1 === undefined ? "sm" : t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      sm: {
        root: "gap-2",
        textWrapper: "",
        label: "text-sm font-medium",
        hint: "text-sm"
      },
      md: {
        root: "gap-3",
        textWrapper: "gap-0.5",
        label: "text-md font-medium",
        hint: "text-md"
      }
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const sizes = t2;
  let t3;
  if ($[8] !== className || $[9] !== size) {
    t3 = renderProps => cx("flex w-max items-start", renderProps.isDisabled && "cursor-not-allowed", sizes[size].root, typeof className === "function" ? className(renderProps) : className);
    $[8] = className;
    $[9] = size;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== hint || $[12] !== label || $[13] !== size || $[14] !== slim) {
    t4 = t5 => {
      const {
        isSelected,
        isDisabled,
        isFocusVisible,
        isHovered
      } = t5;
      return <><ToggleBase slim={slim} size={size} isHovered={isHovered} isDisabled={isDisabled} isFocusVisible={isFocusVisible} isSelected={isSelected} className={slim ? "mt-0.5" : ""} />{(label || hint) && <div className={cx("flex flex-col", sizes[size].textWrapper)}>{label && <p className={cx("text-secondary select-none", sizes[size].label)}>{label}</p>}{hint && <span role="button" tabIndex={0} className={cx("text-tertiary", sizes[size].hint)} onClick={_temp} onKeyDown={_temp2}>{hint}</span>}</div>}</>;
    };
    $[11] = hint;
    $[12] = label;
    $[13] = size;
    $[14] = slim;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== ariaSwitchProps || $[17] !== t3 || $[18] !== t4) {
    t5 = <AriaSwitch {...ariaSwitchProps} className={t3}>{t4}</AriaSwitch>;
    $[16] = ariaSwitchProps;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}